/**
 * Square Footage Calculator Helper
 */
import { pluralize, toDecimal } from '../../helpers/general-helper/general-helper';
import { type ProductPackaging } from '../../types/product.types';

export type SqFtSection = {
	id: number;
	widthFt?: number;
	heightFt?: number;
	widthIn?: number;
	heightIn?: number;
	waste?: number;
};

export type SqFtInformation = {
	sections: SqFtSection[];
	subtotal: number;
	waste: number;
	total: number;
};

/**
 * Default Waste Percentage
 */
const DEFAULT_WASTE = 10; // percentage

/**
 * Resolves and retrieves next available section id.
 */
export const getNextAvailableSectionId = (information: SqFtInformation): number => {
	return information.sections.length > 0 ? information.sections[information.sections.length - 1].id + 1 : 1;
};

/**
 * Generates a new section with default values
 */
export const newSection = (id: number): SqFtSection => {
	return { id, waste: DEFAULT_WASTE };
};

/**
 * Add a new section and returns the updated square footage information.
 * Pure Function: This method always return a new SqFtInformation (immutability)
 */
export const addSection = (section: SqFtSection | {}, information: SqFtInformation): SqFtInformation => {
	return updateInformation({
		...information,
		sections: [...information.sections, { ...newSection(getNextAvailableSectionId(information)), ...section }]
	});
};

/**
 * Remove an existing section and returns the updated square footage information.
 * Pure Function: This method always return a new SqFtInformation (immutability)
 */
export const removeSection = (section: SqFtSection, information: SqFtInformation): SqFtInformation => {
	return updateInformation({ ...information, sections: information.sections.filter((sec) => sec.id !== section.id) });
};

/**
 * Calculates Area in inches for a given section.
 */
export const calculateArea = ({ widthFt = 0, widthIn = 0, heightFt = 0, heightIn = 0 }: SqFtSection): number => {
	if ((widthFt <= 0 && widthIn <= 0) || (heightFt <= 0 && heightIn <= 0)) {
		return 0;
	}
	return (widthFt * 12 + widthIn) * (heightFt * 12 + heightIn);
};

/**
 * Calculate totals for a given section, based on current memoized information.
 */
export const calculateTotals = (memo: SqFtInformation, current: SqFtSection): SqFtInformation => {
	const { widthFt, widthIn, heightFt, heightIn } = current;
	const sqFt = Math.floor(calculateArea(current) / 12 / 12);
	memo.subtotal += sqFt;
	if ((widthFt || widthIn) && (heightFt || heightIn) && current.waste && current.waste > 0) {
		// current.waste is percentage of current area, total (memo.waste) is in sq. ft.
		memo.waste += (current.waste * sqFt) / 100;
	}
	memo.total = memo.subtotal + memo.waste;
	return memo;
};

/**
 * Update Information based on current sections information.
 * Pure Function: This method always return a new SqFtInformation (immutability)
 *
 * Note:
 * If the updated section param is skipped, this method will recalculate the information's subtotal, waste and total
 * properties using the current state of the information object.
 * If the updated section is passed, this method will recalculate the information but also will be introducing
 * the newly state of the updated section.
 */
export const updateInformation = (information: SqFtInformation, updatedSection?: SqFtSection): SqFtInformation => {
	const updatedSections = updateSections(information, updatedSection),
		updated = updatedSections.reduce(calculateTotals, { ...information, subtotal: 0, waste: 0, total: 0 });
	return { ...updated, sections: updatedSections };
};

/**
 * Update and returns all sections.
 * Pure Function: This method always return a new collection of SqFtSection (immutability)
 */
export const updateSections = (information: SqFtInformation, updatedSection?: SqFtSection): SqFtSection[] => {
	return information.sections.map((section, index) => {
		const resolveSection = updatedSection && section.id === updatedSection.id ? updatedSection : section;
		return {
			...resolveSection,
			waste: resolveSection && resolveSection.waste !== null ? resolveSection.waste : DEFAULT_WASTE,
			id: index + 1
		};
	});
};

/**
 * Calculate number of packages (quantity) based on square footage section information.
 */
export const calculatePackages = (information: Pick<SqFtInformation, 'total'>, packaging: ProductPackaging | null | undefined): number => {
	return packaging?.value && packaging.value > 0 && information.total > 0 ? Math.ceil(information.total / packaging.value) : 0;
};

/**
 * Formats Quantity Message
 */
export const formatQuantity = (quantity: number, packaging: ProductPackaging | null | undefined, defaultMessage: string): string => {
	if (packaging && quantity > 0) {
		const { name, value, units } = packaging;
		const total = toDecimal(quantity * value);
		return `${quantity} ${pluralize(name, quantity)}, covers ${total} ${units}`;
	}
	return defaultMessage;
};
